/**
 * OPPLYSNINGENE OM PERSONEN (grep A1, migrering 0914/0915).
 *
 * Standardfeltene ligger på personen. Har hen konto, eier hen dem (`person_details`, «Min profil»);
 * ellers lagrer organisasjonen det den vet i én rad per organisasjon og person (`person_facts`).
 * `app.person_cards` gir de gjeldende verdiene med kilde per felt. Allergier og kosthold ser alle
 * organisasjonens egne folk; pårørende og fødselsdato («de sensitive feltene») bare den som har rolle
 * minst plattformens grense (`app.sees_sensitive_person_fields()`, standard medarbeider), og personen
 * selv (PERSONTYPER-BESTILLING §1, 1077).
 *
 * Allergier og kosthold, førerkort og T-skjorte er tekst (A4, 1026). Tomt er «ikke svart», og sendes som null.
 */
import { z } from 'zod';
import { wireInstant } from './wire-time.js';

/**
 * T-SKJORTESTØRRELSENE SOM FORSLAG (A4, 271.3 a). Svaret er tekst; frivilligskjemaet kan tilby disse som
 * nedtrekk («utfyllingsform»), men lagrer det som står. Allergener, kosthold og førerkort hadde egne
 * kodelister til 22.10 — de er borte, og svarene står som tekst (`food`, `drivingLicence`).
 */
export const TSHIRT_SIZES = ['XS', 'S', 'M', 'L', 'XL', 'XXL', 'XXXL'] as const;

/** Hvem verdien kommer fra: personen selv, eller organisasjonen som leser. */
export const PERSON_FIELD_SOURCES = ['person', 'organization'] as const;
export type PersonFieldSource = (typeof PERSON_FIELD_SOURCES)[number];
export const personFieldSourceSchema = z
  .enum(PERSON_FIELD_SOURCES)
  .meta({ id: 'PersonFieldSource' });

/** Standardfeltene som kan skrives — likt for personen og for organisasjonen. */
export const PERSON_STANDARD_FIELDS = [
  'instrument',
  'basedIn',
  'tshirtSize',
  'drivingLicence',
  'dateOfBirth',
  'food',
  'emergencyName',
  'emergencyPhone',
  'emergencyRelation',
] as const;
export type PersonStandardField = (typeof PERSON_STANDARD_FIELDS)[number];

/**
 * De sensitive feltene (1077): pårørende og fødselsdato. Bak plattformens grense, også når de
 * skrives — et skjema som ikke viser dem, kan ikke sende dem.
 */
export const PERSON_SENSITIVE_FIELDS = [
  'dateOfBirth',
  'emergencyName',
  'emergencyPhone',
  'emergencyRelation',
] as const satisfies readonly PersonStandardField[];

const text = (max: number) =>
  z
    .string()
    .trim()
    .max(max)
    .nullable()
    .transform((value) => (value === null || value === '' ? null : value));

const isoDate = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/)
  .refine((value) => value >= '1900-01-01', { message: 'date_of_birth_too_early' })
  .nullable();

/** Feltene slik de skrives — hvert av dem kan utelates (PATCH) eller tømmes med null. */
export const personStandardFieldsPatchSchema = z
  .object({
    instrument: text(120).optional(),
    basedIn: text(120).optional(),
    tshirtSize: text(40).optional(),
    drivingLicence: text(120).optional(),
    dateOfBirth: isoDate.optional(),
    /** «Allergier og kosthold» som én tekst (A4). Kjøkkenlista teller like svar slik de er skrevet. */
    food: text(1500).optional(),
    emergencyName: text(120).optional(),
    emergencyPhone: text(40).optional(),
    emergencyRelation: text(60).optional(),
  })
  .meta({ id: 'PersonStandardFieldsPatch' });
export type PersonStandardFieldsPatch = z.infer<typeof personStandardFieldsPatchSchema>;

/** `PATCH /v1/people/:personId/card` — organisasjonens opplysninger (`person_facts`). */
export const updatePersonCardRequestSchema = personStandardFieldsPatchSchema.meta({
  id: 'UpdatePersonCardRequest',
});
export type UpdatePersonCardRequest = z.infer<typeof updatePersonCardRequestSchema>;

export const personCardSourcesSchema = z
  .object({
    displayName: personFieldSourceSchema.optional(),
    phone: personFieldSourceSchema.optional(),
    email: personFieldSourceSchema.optional(),
    instrument: personFieldSourceSchema.optional(),
    basedIn: personFieldSourceSchema.optional(),
    tshirtSize: personFieldSourceSchema.optional(),
    drivingLicence: personFieldSourceSchema.optional(),
    dateOfBirth: personFieldSourceSchema.optional(),
    food: personFieldSourceSchema.optional(),
    emergencyName: personFieldSourceSchema.optional(),
    emergencyPhone: personFieldSourceSchema.optional(),
    emergencyRelation: personFieldSourceSchema.optional(),
  })
  .meta({ id: 'PersonCardSources' });
export type PersonCardSources = z.infer<typeof personCardSourcesSchema>;

/** `GET /v1/people/:personId/card` — gjeldende verdier, med kilde per felt. */
export const personCardSchema = z
  .object({
    personId: z.uuid(),
    contactId: z.uuid().nullable(),
    hasAccount: z.boolean(),
    displayName: z.string(),
    givenName: z.string().nullable(),
    familyName: z.string().nullable(),
    phone: z.string().nullable(),
    email: z.string().nullable(),
    instrument: z.string().nullable(),
    basedIn: z.string().nullable(),
    tshirtSize: z.string().nullable(),
    drivingLicence: z.string().nullable(),
    /** Null både når feltet er tomt og når den som leser ikke ser det — se `sensitiveVisible`. */
    dateOfBirth: z.string().nullable(),
    food: z.string().nullable(),
    emergencyName: z.string().nullable(),
    emergencyPhone: z.string().nullable(),
    emergencyRelation: z.string().nullable(),
    /** Ser den som leser pårørende og fødselsdato (1077)? Personen selv gjør alltid det. */
    sensitiveVisible: z.boolean(),
    /** Kan den som leser skrive organisasjonens opplysninger (`contact:update`)? */
    canEdit: z.boolean(),
    sources: personCardSourcesSchema,
  })
  .meta({ id: 'PersonCard' });
export type PersonCard = z.infer<typeof personCardSchema>;

/** Det én organisasjon har registrert om meg, og som er noe annet enn det jeg har selv (253.12). */
export const registeredFactsSchema = z
  .object({
    factsId: z.uuid(),
    organizationId: z.uuid(),
    organizationName: z.string(),
    fields: personStandardFieldsPatchSchema,
    updatedAt: wireInstant,
  })
  .meta({ id: 'RegisteredFacts' });
export type RegisteredFacts = z.infer<typeof registeredFactsSchema>;

export const registeredFactsListSchema = z
  .object({ items: z.array(registeredFactsSchema) })
  .meta({ id: 'RegisteredFactsList' });
export type RegisteredFactsList = z.infer<typeof registeredFactsListSchema>;
